import { useMemo, useState } from 'react';

import { usePlanEditor } from '../editor';

import { buildTimelineModel } from './timelineModel';
import type { TimelineModel } from './timelineModel';

import { todayIso } from '@/lib';

/**
 * The timeline model, rebuilt once per document revision — never per pointer
 * frame. A drag reads its payload and clamp bounds out of this once, on
 * pointerdown.
 *
 * The window is anchored to a SEED captured at mount and unioned with the
 * currently-derived one, so it can only grow. Without that anchor, dragging the
 * only bar in a plan 30+ days later would move the plan's extent, the padded
 * window would follow, every offset would shift and the bar would visually jump
 * away from the pointer mid-drag. Unioning against a fixed seed also means
 * `startDay` never moves later, which is the part that shifts offsets.
 *
 * A seed rather than state-in-an-effect: no cascading render, and nothing reads a
 * ref during render. `PlanTimeline` is keyed on the project id, so the seed is
 * re-captured when a different project loads instead of inheriting its range.
 */
export const useTimelineModel = (): TimelineModel => {
  const { state } = usePlanEditor();
  // Read once per mount: it only changes at midnight, and a stable value keeps
  // the memo below honest.
  const [today] = useState(todayIso);
  const [seed] = useState(() => buildTimelineModel(state.document.plan, today).window);

  return useMemo(
    () => buildTimelineModel(state.document.plan, today, seed),
    [state.document.plan, today, seed],
  );
};
